//	+-----------------------------------------------+
//	| Cardboard UI controller objects				|
//	+-----------------------------------------------+

/**
 * @file cardboard/mainWorkspaceController.js
 * @module cardboard/mainWorkspaceController
 * @desc This file contains the controllers module.
 * @author Geoffrey Pursell
 */

/* global workspace, settings */

define('cardboard/mainWorkspaceController',[
    // libraries
    "jquery", "underscore", "backbone", "store", "underscore.string", "rsvp", "bowser",

    // app modules
    "cardboard/baseController", "cardboard/dataBoxController", "cardboard/miscControllers", "cardboard/models", "cardboard/collections", "commonDataSources", "cardboard/baseViews", "cardboard/databoxes",
    "cardboard/util", "cardboard/pluginHelper"
],
    function ($, _, Backbone, Store, _s, RSVP, Bowser,
        BaseController, DataBoxController, MiscControllers, Models, Collections, CommonDataSources, Views, DataBoxViews,
        Util, PluginHelper) {

        console.log("MODULE: cardboard/mainWorkspaceController");

        var MainWorkspaceController,
            Promise = RSVP.Promise;

        /**
         * @constructor
         * @alias module:cardboard/mainWorkspaceController
         */
        MainWorkspaceController = function () {
            this.initialize.apply(this, arguments);
        };

        Util.inherit(BaseController, MainWorkspaceController, /** @lends module:cardboard/mainWorkspaceController.prototype */ {

            /** the current state of the workspace; either a client-side content
             * page URL, described by its URL string, or a report, described by its
             * model object, id, and plugin name. Hopefully we can simplify this.
             * @type Object
             * @example
             * // default is this:
             * {
             *     reportModel: null,
             *     reportPluginName: null,
             *     pageURL: "/"
             * } */
            currentState: {
                reportModel: null,
                reportPluginName: null,
                pageURL: "/"
            },

            /** declarative Backbone events
             * @type Object*/
            globalEvents: {
                "reportTapped": "onReportTap",
                "paneOpened": "onPaneOpened",
                "paneClosed": "onPaneClosed",
                "globalSettings:changed": "onGlobalSettingChanged",
                "addDataBox": "addBox",
                "dataBoxDestroyed": "onDataBoxDestroyed",
                "orgChanged": "onOrgChanged",
                "filterChanged": "onFilterChanged",
                "workspaceURLChanged": "updateSocialLinks",
                "beforeHamburgerDrawerOpen": "onBeforeHamburgerDrawerOpen",
                "hamburgerDrawerOpened": "onHamburgerDrawerOpened",
                "hamburgerDrawerClosed": "onHamburgerDrawerClosed",
                "fixedHeaderShouldShow": "onFixedHeaderShouldShow",
                "fixedHeaderShouldHide": "onFixedHeaderShouldHide"
            },

            /** constructor
             * @fires beforeHamburgerDrawerOpen
             * @fires hamburgerDrawerClosed
             * @fires hamburgerDrawerOpened */
            initialize: function (dependencies) {

                // attach delcarative events
                this.attachGlobalEvents();

                /** init the dataBoxControllers array
                 * @type Array */
                this.dataBoxControllers = [];

                /** store instances of filter sources shared across data boxes
                 * @type Object */
                this.sharedFilterSources = {};

                // set debug mode? maaaaybe
                this.setDebugMode();

                // attach all the provided dependencies!
                // THIS IS A BIG DEAL
                _.extend(this, dependencies);

                // render it now, maybe we've got the data already.
                this.reportListView.render();

                /** just for convenience
                 * @type jQuery */
                this.$jqmPageContainer = $(":mobile-pagecontainer");

                // listen for the hamburger drawer opening & closing and generate global events
                this.$navDrawer
                    .on("panelbeforeopen", function () {
                        Backbone.trigger("beforeHamburgerDrawerOpen");
                    })
                    .on("panelclose", function () {
                        Backbone.trigger("hamburgerDrawerClosed");
                    })
                    .on("panelopen", function () {
                        Backbone.trigger("hamburgerDrawerOpened");
                    });

                // manually handle the nav drawer control so we can customize behavior
                $(".drawerToggle").on("click", this.onDrawerToggleClicked.bind(this));

                this.$introLink.on("click", this.onIntroLinkClicked.bind(this));

                this.createModalInfoDialog();

                // listen for keyboard events that get all the way to the body
                $(window).on("keypress", this.onKeyPress.bind(this));

                // listen for page change events so we can poke the SVGs to resive
                // themselves when the settings dialog closes
                this.$jqmPageContainer.on("pagecontainershow", this.onJQMPageShown.bind(this));
                this.$jqmPageContainer.on("pagecontainerbeforeshow", this.beforeJQMPageShown.bind(this));
            },

            /** @private */
            createModalInfoDialog: function () {
                /** the modal info dialog used for info buttons taps and such.
                 * @type jQuery
                 */
                this.$modalInfoDialog = $("#popup_reportInfo").popup({
                    tolerance: "60,40,40,40",
                    positionTo: "origin",
                    transition: "pop",
                    afterclose: function () {
                        $('#workspace').focus();
                    }
                });

                this.$modalInfoDialog.children("a").button();
            },

            // EVENT HANDLERS

            /** custom event handler: someone has tapped a report!
             * @param {Object} report A report's object
             */
            onReportTap: function (tapInfo) {
                if (tapInfo.reportModel) {
                    this.setReport(tapInfo.reportModel);
                    window.charts = [];
                    window.count = -1;
                } else if (tapInfo.pageURL !== "#" || tapInfo.pageID) {
                    $('#' + tapInfo.pageId).focus();
                    this.setWorkspacePage(tapInfo);
                }
                window.tappedReport = tapInfo.reportModel.attributes.url;
            },
            /** custom event handler: someone has opened a pane! We might
             * need to respond by scrolling the viewport.
             * @event paneOpened
             * @param {Object} paneView The view object for the pane that
             * was just opened.
             */
            onPaneOpened: function (info) {
                this.updateWorkspaceURL();

                if (info.necessaryScroll) {
                    _.defer(function () {
                        Util.scrollToElement(info.paneView.$el);
                    });
                }
            },
            /** @event paneClosed */
            onPaneClosed: function () {
                this.updateWorkspaceURL();
            },
            /** @event globalSettingChanged */
            onGlobalSettingChanged: function (setting) {
                if (setting.name === "debugMode") {
                    this.setDebugMode();
                }
            },
            /** @event beforeHamburgerDrawerOpen
             * @fires bubblesShouldClose */
            onBeforeHamburgerDrawerOpen: function () {
                Backbone.trigger("bubblesShouldClose");
            },
            /** @event hamburgerDrawerOpened */
            onHamburgerDrawerOpened: function () {
                // manage keyboard focus: set focus to first available report
                this.reportListView.focusFirstReportTile();
            },
            /** @event hamburgerDrawerClosed */
            onHamburgerDrawerClosed: function () {
                // work around an apparent jqm bug
                $(".drawerToggle").removeClass("ui-btn-active");

                // manage keyboard focus: set focus to "circle i" question
                // description button
                if (this.questionBarView.isVisible()) {
                    this.questionBarView.focusInfoButton();
                } else {
                    this.focusDrawerToggle();
                }
            },

            /** @event fixedHeaderShouldShow */
            onFixedHeaderShouldShow: function () {
                this.showFixedHeader();
            },
            /** @event fixedHeaderShouldHide */
            onFixedHeaderShouldHide: function () {
                this.hideFixedHeader();
            },

            /** @event keyPress */
            onKeyPress: function (event) {
                if (event.keyCode === 27) { // ESC key
                    Backbone.trigger("bubblesShouldClose");
                    Backbone.trigger("drawersShouldClose");
                }

            },
            /** @event introLinkClicked */
            onIntroLinkClicked: function (e) {
                window.appRouter.navigate("/", {
                    trigger: true
                });
                e.preventDefault();
                e.stopPropagation();
                return false;
            },
            /** @event firstOrgSet */
            onFirstOrgSet: function () {
                this.showExtraUI();
            },
            /** @event dataBoxDestroyed */
            onDataBoxDestroyed: function (dataBoxController, options) {
                if (options && options.removeNow) {
                    this.removeBox(dataBoxController);
                }
            },
            /** @event orgChanged */
            onOrgChanged: function () {
                this.updateWorkspaceURL();
            },
            /** @event filterChanged */
            onFilterChanged: function () {
                this.updateWorkspaceURL();
            },
            /** @event drawerToggleClicked */
            onDrawerToggleClicked: function ( /*event*/) {

                var targ = $("#navDrawer");
                if ($(document.body).is(".responsivePanel") && !this.navigationIsOffscreen()) {
                    //  Util.scrollToPixel(0);

                } else {
                    targ.panel("toggle");
                }
                // Util.scrollToPixel(0);
                targ.focus();
            },
            /** @event JQMPageShown */
            onJQMPageShown: function ( /*event, ui*/) {
                var $activePage = this.$jqmPageContainer.pagecontainer("getActivePage");
                // this solves bug #183 the cheap and easy way.
                if ($activePage.is("#page_workspace")) {
                    $(window).trigger("resize");
                }
            },
            /** @event beforeJQMPageShown */
            beforeJQMPageShown: function () {
                Backbone.trigger("bubblesShouldClose");
            },

            // INFO

            /** tells you whether or not the side panel navigation drawer is
             * offscreen due to CSS breakpoints or not. Currently, we do this by
             * looking at the left-margin of the panel wrapper. It's indirect, but
             * it lets us keep the state in the CSS breakpoint.
             * @returns {boolean}  */
            navigationIsOffscreen: function () {
                var leftMargin = parseInt($(".ui-panel-wrapper").css("margin-left"), 10);
                return (leftMargin < 100);
            },

            /** tells you if the drawer needs to close -- that is, if the drawer is marked as being open, and as best we can tell, we're not in the responsive mode where it's onscreen at all times.
             * @returns {boolean}
             */
            drawerNeedsToClose: function () {
                return this.$navDrawer.is(".ui-panel-open") &&
                    this.navigationIsOffscreen();
            },
            /** @returns {Number} the number of data boxes currently live */
            boxCount: function () {
                return this.dataBoxControllers.length;
            },
            /** @returns {Boolean} whether or not an org has been selected yet */
            anOrgHasBeenSelected: function () {
                return (this.dataBoxControllers.length &&
                    this.dataBoxControllers[0].dataLoaded);
            },
            /** if you know only of a data box's view, but need to find its
             * controller, this method is for you.
             * @param {Object} dataBoxView the view of a databox whose controller
             * you need
             * @returns {Object} the controller corresponding to the data box's
             * view */
            getDataBoxControllerFromView: function (dataBoxView) {
                return _(this.dataBoxControllers)
                    .find(function (controller) {
                        return (controller.view === dataBoxView);
                    });
            },
            /** @returns {String} the name of the current report plugin */
            getCurrentPluginName: function () {
                return this.currentState.reportPluginName;
            },
            /** @returns {String} the code (it's just an ID) of the current report plugin */
            getCurrentPluginCode: function () {
                return this.currentState.reportModel.get("code");
            },
            /** @returns {Object} the reportData model object that describes the current report plugin */
            getCurrentPluginModel: function () {
                return this.currentState.reportModel;
            },
            /** constructs and returns the correct deep-linking URL for the current state of the workspace.
             * @returns {String} */
            getWorkspaceURL: function () {
                if (this.currentState.pageURL) {
                    return this.currentState.pageURL;
                } else {
                    var workspaceURL,
                        dbArray = _(this.dataBoxControllers).map(function (dbController) {
                            var urlFragments = [],
                                blacklist = ["orgName"],

                                filterParams = _.omit(dbController.view.getConfigParams(), blacklist),
                                normalParams = {},
                                checkboxParams = {};

                            if (filterParams.orgId === "") {
                                return null;
                            }

                            // encode in our URL scheme: replace spaces with
                            // pluses, and put a -- between key and value
                            _(filterParams).each(function (value, name) {
                                if (value instanceof Array) {
                                    checkboxParams[name] = value;
                                } else {
                                    normalParams[name] = value;
                                }
                            });

                            // do the normal fragments first
                            urlFragments = _(normalParams).map(function (value, key) {
                                return key.replace(/ /g, "+") + "--" + value.replace(/ /g, "+");
                            }).value();

                            // now, add the checkbox arrays in
                            _(checkboxParams).each(function (checkboxValues, paramName) {
                                var paramString = paramName + "--" + checkboxValues.join("%7C");
                                // encode spaces
                                urlFragments.push(paramString.replace(/ /g, "+"));
                            });

                            // add the pane state
                            urlFragments.push("p--" + dbController.view.getPaneState());

                            // join pairs with double underscores between them
                            return urlFragments.join("__");
                        }).compact().value();

                    workspaceURL = this.getCurrentPluginName() + "/" + dbArray.join("/");

                    return workspaceURL;
                }
            },
            /** @param {String} pageID
             * @returns {Boolean} */
            workspacePageExists: function (pageID) {
                return !!$("#workspace > .page." + pageID).length;
            },

            // ACTIONS

            /** set the URL to match the current workspace state without triggering
             * any actions
             */
            updateWorkspaceURL: function () {
                var url = this.getWorkspaceURL();
                window.appRouter.navigate(url, {
                    trigger: false
                });
                this.saveStateInLocalStorage();
                Backbone.trigger('workspaceURLChanged');
            },
            /** this method is deprecated because doing a replace appears to destroy
             * the browser history from that point forward. */
            updateWorkspaceURLWithoutHistory: function () {
                window.appRouter.navigate(this.getWorkspaceURL(), {
                    trigger: false,
                    replace: true
                });
                this.saveStateInLocalStorage();
            },
            /** Save the workspace state in local storage; we need this because iOS
             *  dumps the app state every time the app loses focus when running as a
             *  homescreen web app. We have to be able to detect a saved state and
             *  restore it. */
            saveStateInLocalStorage: function () {
                if (window.navigator.standalone) {
                    Store.set("lastURL", this.getWorkspaceURL());
                }
            },

            /** change the jQuery Mobile page
             * @param {String} pageContainerSelector - the html id of an element that defines a page
             * @param {Object} options - the options hash to pass to jQuery Mobile
             * */
            changePage: function (pageContainerSelector, options) {
                // this method is apparently subject to change in 1.4, and will be
                // removed in 1.5
                $.mobile.changePage(pageContainerSelector, options);

                // this is my guess at the new method.
                // $("#pageContainer").pagecontainer("changePage", pageContainerSelector, options);
            },
            /** sets debug mode, which makes debugging toolbard appear all over the
             * UI, allowing developers to inspect underlying JSON at will */
            setDebugMode: function () {
                var setting = settings.get("debugMode");
                if (setting === "on") {
                    $(document.body).addClass("debugMode");
                } else {
                    $(document.body).removeClass("debugMode");
                }
            },

            /** action: reconstruct an entire workspace. This will optimize for a
             * few common cases, but will otherwise fall back to a complete teardown
             * and rebuild of the workspace.
             * @param {String} reportCodeOrPageID - A report's model object, numeric
             * ID, or lowercase string code, or the ID of a workspace page
             * @param {Array} boxes - array of objects, each with parameters to feed
             * into a new box
             */
            reconstruct: function (reportCodeOrPageID, boxes) {
                if (PluginHelper.pluginExists(reportCodeOrPageID)) {
                    this.reconstructReportPage(reportCodeOrPageID, boxes);
                } else if (this.workspacePageExists(reportCodeOrPageID)) {
                    this.setWorkspacePage(reportCodeOrPageID, true);
                } else {
                    var mess = "Sorry, no page or report called \"" + reportCodeOrPageID + "\" exists.";
                    console.log(mess);
                    alert(mess);
                }
            },

            /** this is called by reconstruct(); it performs an optimized
             * reconstruction of a report page, doing as little work as possible to
             * restore the workspace to the state described by the deep linking URL.
             * @param {String} reportCodeOrPageID - A report's model object, numeric
             * ID, or lowercase string code, or the ID of a workspace page
             * @param {Array} boxes - array of objects, each with parameters to feed
             * into a new box
             * @private */
            reconstructReportPage: function (reportCodeOrPageID, boxes) {
                var setBoxState = function () {
                    _(boxes).each(function (params, index) {
                        var dbController = this.dataBoxControllers[index],
                            preexistingParams = dbController.view.getConfigSelections();

                        dbController.setViewStateFromParams(params);
                        if (Util.Data.paramsAreDifferent(preexistingParams, params)) {
                            dbController.loadReportDataSources();
                        }
                    }, this);
                }.bind(this);

                if (this.currentState.reportPluginName === reportCodeOrPageID) {
                    // we can optimize if: the current report hasn't changed...
                    if (boxes.length === this.dataBoxControllers.length) {
                        // ...and if we have the same number of boxes
                        setBoxState();
                        return;
                    } else if (boxes.length === this.dataBoxControllers.length - 1) {
                        // ...or if we have one fewer boxes
                        this.removeBox(_.last(this.dataBoxControllers).destroy());
                        setBoxState();
                        return;
                    } else if (boxes.length === this.dataBoxControllers.length + 1) {
                        // ...or if we have one more box
                        this.addBox(_.last(boxes)).filtersLoadedPromise
                            .then(function () {
                                setBoxState();
                            }.bind(this))
                            .then(null, Util.reportPromiseException);
                        return;
                    }
                }

                // we don't (yet?) know how to efficiently set the report page state
                // for this case, or we can't; wipe the report page state and start
                // over
                this.removeAllBoxes();
                this.currentState = {};
                this.setReport(reportCodeOrPageID, !!boxes);

                // add the boxes we were asked for
                _(boxes).each(function (boxParams) {
                    this.addBox(boxParams);
                }.bind(this));

                // unless the plugin asks us nicely not to, show the
                // help bubble
                if (!PluginHelper.getCurrentPluginOptions().noHelpBubble) {
                    // this.showHeaderIntroBubble();    //  Turn this off for now
                }

                window.isCollegePrevReport = window.isCollegeReport;

                // console.log(reportCodeOrPageID);

                if (reportCodeOrPageID == "collegeGraduates" || reportCodeOrPageID == "newCollegeStudentDemographics" || reportCodeOrPageID == "newCollegeStudentEnrollment") {
                    $(".orgLabel").html("<span class='main'>College or System</span>");

                    window.isCollegeReport = true;
                } else if (reportCodeOrPageID == "HSGraduatesToEmployment") {
                    $(".orgLabel").html("<span class='main'>District</span>");
                    window.isCollegeReport = false;
                }
                else if (reportCodeOrPageID == "collegeTransfers") {
                    $(".orgLabel").html("<span class='main'>Previous College or System</span>");
                    window.isCollegeReport = true;
                } else if ((reportCodeOrPageID == "demographics") || (reportCodeOrPageID == "preschoolParticipation")) {
                    $(".orgLabel").html("<span class='main msg_schDist'>School, District <br/> or County</span>");
                }

                else if (reportCodeOrPageID == "headStartParticipation") {
                    $(".orgLabel").html("<span class='main msg_headStartAg'>Head Start Agency</span>");
                } 
                else if (reportCodeOrPageID == "abeEducationOutcomes") {
                    $(".orgLabel").html("<span class='main'>ABE Program or Consortium</span>");
                } 
                else  if (reportCodeOrPageID == "highSchoolAcademics" || reportCodeOrPageID == "collegeActivity" || reportCodeOrPageID == "stayingInAndCompletingCollege" || reportCodeOrPageID == "developmentalEducation" || reportCodeOrPageID == "courseTaking" || reportCodeOrPageID == "electivesToACTResults" || reportCodeOrPageID == "outStateCollegeStudentEnrollment")  {
                    $(".orgLabel").html("<span class='main msg_schOrDist'>School or District</span><span class='note'>(Enter all 4 digits with leading zeros as needed for the district number)</span>");
                }
                else {
                    $(".orgLabel").html("<span class='main msg_schOrDist'>School or District</span>");
                }

                var hash = location.hash;

                if (hash.indexOf("engagementAndSafety") > -1) {
                    setTimeout(function () {
                        $("table tr.dividerAbove").each(function () {
                            var cell = $.trim($(this).find('td').text());
                            if (cell.length == 0) {
                                $(this).addClass('noborder');
                            }
                        });
                    }, 1000);
                }
            },

            /** action: changes all data boxes on the workspace to reflect a newly
             * selected report.
             * @param {Object | String | Number} report A report's
             * model object, numeric ID, or lowercase string code
             */
            setReport: function (report, reconstructing) {
                var plugin, reportPluginName, reportModel, pluginOptions, newState,
                    self = this;

                try {
                    // if either getting the report metadata or the
                    // report plugin throws an exception, it will
                    // get us out of here.
                    // first: figure out what the heck we were passed
                    newState = {
                        reportModel: reportModel = CommonDataSources.reportInfo.findReport(report),
                        reportPluginName: reportPluginName = reportModel.get("url"),
                        pageURL: null,
                        pageID: null
                    };
                    plugin = PluginHelper.getPlugin(reportPluginName);

                    // fire events for anyone who cares
                    Backbone.trigger("beforeWorkspaceStateChange", newState);
                    Backbone.trigger("bubblesShouldClose");
                    Backbone.trigger("beforeReportSet", reportModel);
                } catch (exception) {

                    // var mess = "Error setting report: " + exception.message;
                    var mess = "This plugin is not yet implemented.";
                    console.log(mess);
                    alert(mess);

                    // we need to restore the report highlight. note that the passed
                    // report is NOT the one that failed, it's the one that was
                    // there before the failed attempt
                    Backbone.trigger("workspaceStateChangeFailed", self.currentState);

                    return;
                }

                // let's see the boxes, don't you think?  this is outside the below
                // if statement because if you're coming back to a report from a
                // content page, it still needs to show the boxes section even if
                // we're not loading up a new report
                self.showWorkspacePage("dataBoxes");

                // if this is the already-selected report, do nothing
                if (self.currentState.reportPluginName !== reportPluginName) {

                    // update our accounting of the current report
                    self.currentState = newState;
                    // this is here to handle the case where the simplified My
                    // School was the first report we hit, so the extra UI is
                    // hidden, but no selection was made (which re-shows it) before
                    // changing reports. Gotta show that extra UI.  If we're
                    // reconstructing the workspace from a URL, we don't want to do
                    // any of this.
                    pluginOptions = PluginHelper.getCurrentPluginOptions();
                    if (pluginOptions.hideExtraUIUntilOrgSelection && !self.anOrgHasBeenSelected() && !reconstructing) {
                        self.hideExtraUI();
                    } else {
                        self.showExtraUI();
                    }

                    var toDoWhenDrawerIsClosed = function () {
                        // start off by loading shared data sources, right away
                        self.prepareSharedFilterSources();
                        var dataBoxReadyPromises = self.loadSharedFilterSources();

                        // re-filter common data sources for the current report
                        self.commonDataSourceManager.allSourcesReady
                            .then(function () {
                                // self.loadSharedFilterSources();

                                return self.commonDataSourceManager.filterCommonDataSourcesForReport(reportModel);
                            })
                            .then(function () {
                                if (!reconstructing) {
                                    if (self.dataBoxControllers.length === 0) {
                                        // make a box if we need one.  it's important that this be done
                                        // AFTER the reportSet event above, or else the report data
                                        // sources will get loaded twice and stuff won't work right.
                                        var newBox = self.addBox();

                                        // if the plugin has requested that extra UI be hidden
                                        // until an org selection has been made, let's do
                                        // that. we need to listen for only the first selection
                                        // in the first databox.
                                        if (pluginOptions.hideExtraUIUntilOrgSelection) {
                                            newBox.view.once("orgChanged", self.onFirstOrgSet, self);
                                        }
                                    } else {
                                        _(self.dataBoxControllers).each(function (dbController) {
                                            dataBoxReadyPromises.push(dbController.onReportSet());
                                        });
                                    }
                                    return Promise.all(dataBoxReadyPromises);
                                } else {
                                    return Promise.resolve();
                                }
                            })
                            .then(function () {
                                // unless the plugin asks us nicely not to, show the
                                // help bubble
                                //    if (!pluginOptions.noHelpBubble) {
                                //       self.showHeaderIntroBubble();
                                //   }
                                if (!reconstructing) {
                                    self.updateWorkspaceURL();
                                }

                            })
                            .then(null, Util.reportPromiseException);
                    };

                    if (self.drawerNeedsToClose()) {
                        Backbone.once("hamburgerDrawerClosed", toDoWhenDrawerIsClosed.bind(self));
                        self.closeDrawer();
                    } else {
                        toDoWhenDrawerIsClosed();
                    }
                }
              
                var repName = self.currentState.reportPluginName;

                _.delay(function () {

                    if (self.dataBoxControllers.length > 0) {

                        window.isCollegePrevReport = window.isCollegeReport;
                        var currentOrg = self.dataBoxControllers[0].view.getOrg();

                        // In SLEDS if we are switching from Colleges to HS or vice-versa and a school/college is selected we need to set the default Org (statewide)
                        if (repName == "newCollegeStudentDemographics" || repName == "newCollegeStudentEnrollment" || repName == "collegeGraduates") {

                            if (!window.isCollegeReport) {

                                if (currentOrg === undefined || currentOrg.orgID != "999999000") {

                                    _(self.dataBoxControllers).each(function (dbController) {
                                        dbController.setDefaultOrg();
                                        setTimeout(function () {
                                           // console.log("THIS");
                                            //  Backbone.trigger("orgChanged");
                                            Backbone.trigger("reportSet");
                                        }, 1000);

                                    });
                                }
                                else
                                {
                                    setTimeout(function () {
                                        //console.log("THIS2");
                                        //  Backbone.trigger("orgChanged");
                                        Backbone.trigger("reportSet");
                                    }, 1000);
                                }
                            }
 
                            window.isCollegeReport = true;
                        } else if (repName == "HSGraduatesToEmployment") {
                            $(".orgLabel").html("<span class='main msg_dist'>District</span>");
                            window.isCollegeReport = false;

                            if (currentOrg === undefined || window.isCollegeReport) {
                                if (currentOrg.orgID != "999999000") {

                                    _(self.dataBoxControllers).each(function (dbController) {
                                        dbController.setDefaultOrg();

                                        setTimeout(function () {
                                            //  Backbone.trigger("orgChanged");
                                            Backbone.trigger("reportSet");
                                        }, 1000);
                                    });
                                }
                            }
                        } 
                       
                        else {
                            if (repName == "highSchoolAcademics" || repName == "collegeActivity" || repName == "stayingInAndCompletingCollege" || repName == "developmentalEducation" || repName == "courseTaking" || repName == "electivesToACTResults" || repName == "outStateCollegeStudentEnrollment")  {
                                $(".orgLabel").html("<span class='main msg_schOrDist'>School or District</span><span class='note'>(Enter all 4 digits with leading zeros as needed for the district number)</span>");
                            }
                            else if (repName == "abeEducationOutcomes") {
                                $(".orgLabel").html("<span class='main'>ABE Program or Consortium</span>");
                            } 
                            else if (repName == "collegeTransfers") {
                                $(".orgLabel").html("<span class='main'>Previous College or System</span>");
                            }
                            if (currentOrg === undefined || window.isCollegeReport) {
                                if (repName !== "mySchool") {
                                    _(self.dataBoxControllers).each(function (dbController) {
                                        dbController.setDefaultOrg();

                                        setTimeout(function () {
                                            Backbone.trigger("orgChanged");
                                            Backbone.trigger("reportSet");
                                        }, 1000);
                                    });
                                }
                            }
                            window.isCollegeReport = false;
                        }
                    }
                }, 2000);
            },

            updateSocialLinks: function () {
                var socialContainer = $('#socialListContainer');
                if (socialContainer.length > 0) {
                    var encURL = encodeURIComponent(window.location.href);

                    socialContainer.html('<ul class="socialList st_sharethis_custom"><li><a href="https://www.facebook.com/sharer/sharer.php?u=' + encURL + '" target="_blank"><span class="st_facebook"></span></a></li><li><a href="http://twitter.com/share?text=Minnesota%20Report%20Card/&url=' + encURL + '" target="_blank"><span class="st_twitter"></span></a></li><li><a href="https://plus.google.com/share?url=' + encURL + '" target="_blank"><span class="st_googleplus"></span></a></li><li><a href="mailto:?subject=Minnesota Report Card&body=Report Link: ' + encURL + '"><span class="st_email"></span></a></li></ul>');
                    // stButtons.makeButtons();
                }
            },

            loadSharedFilterSources: function () {
                // make the requests, and gather their promises
                // if any came back with errors, we should update the GUI to say so.
                return _(this.sharedFilterSources).invoke("fetch").value();
            },

            getPreparedSharedFilterSources: function () {
                return this.sharedFilterSources;
            },

            /** instatiates the selected report's data sources and saves
             * them in a hash */
            prepareSharedFilterSources: function () {
                var sharedFilterSources = PluginHelper.getCurrentPluginSharedFilterSources();
                this.sharedFilterSources = {};

                _(sharedFilterSources).each(function (source, sourceName) {
                    if (source instanceof Models.Model || source instanceof Collections.Collection) {
                        // it's a pointer to an instantiated data source, just shove
                        // it into the databox
                        this.sharedFilterSources[sourceName] = source;
                    } else {
                        // it's not a pointer to an instantiated data source, it's a
                        // data source info object, so let's use it to fire one up.
                        if (source.enabled) {
                            this.sharedFilterSources[sourceName] = Util.Data.instantiateSource(source);
                        }
                    }
                }, this);
            },

            /** action: changes the workspace to show a content page rather than a
             * report page. this should have a lot in common with the above
             * setReport().
             * @param {Object | String} workspace state object, containing a page URL
             * property, or a simple string of the page ID
             * @param {Boolean} whether or not we are reconstructing a workspace
             */

            setWorkspacePage: function (newState, reconstructing) {
                var newPageURL, newPageID;

                if (typeof newState === "string") {
                    newPageID = newState;
                    newState = {
                        pageURL: null,
                        pageID: newPageID
                    };
                } else {
                    newPageURL = newState.pageURL || "/" + newState.pageID;
                    newPageID = newState.pageID ||
                        (newPageURL && newPageURL.replace(/^\//, ""));
                }

                try {
                    Backbone.trigger("beforeWorkspaceStateChange", newState);
                    Backbone.trigger("bubblesShouldClose");

                    if (this.workspacePageExists(newPageID)) {
                        this.showWorkspacePage(newPageID);
                    } else {
                        throw "page doesn't exist";
                    }
                    this.currentState = newState;
                } catch (exception) {
                    //console.log(newState.pageURL);
                    if (newState.pageURL == "https://education.mn.gov/Maps/ECLDS/") {
                        window.location.href = "https://education.mn.gov/Maps/ECLDS/";
                        return;
                    } else if (newState.pageURL == "https://education.mn.gov/MDE/Data/") {
                        window.location.href = "https://education.mn.gov/MDE/Data/";
                        return;
                    } else if (newState.pageURL == "https://education.mn.gov/Maps/CompSvcs/") {
                        window.location.href = "https://education.mn.gov/Maps/CompSvcs/";
                        return;
                    } else if (newState.pageURL == "http://www.ohe.state.mn.us/mPg.cfm?pageID=1935") {
                        window.location.href = "http://www.ohe.state.mn.us/mPg.cfm?pageID=1935";
                        return;
                    } else if (newState.pageURL == "https://mn.gov/deed/data/") {
                        window.location.href = "https://mn.gov/deed/data/";
                        return;
                    } else if (newState.pageURL == "http://eclds.mn.gov/") {
                        window.location.href = "http://eclds.mn.gov/";
                        return;
                    } else if (newState.pageURL == "https://public.govdelivery.com/accounts/MNOHE/subscriber/new?topic_id=MNOHE_62") {

                        var win = window.open("https://public.govdelivery.com/accounts/MNOHE/subscriber/new?topic_id=MNOHE_62", '_blank');
                        win.focus();
                        return;
                    } else if (newState.pageURL == "https://public.govdelivery.com/accounts/MNOHE/subscriber/new?topic_id=MNOHE_C14") {

                        var win = window.open("https://public.govdelivery.com/accounts/MNOHE/subscriber/new?topic_id=MNOHE_C14", '_blank');
                        win.focus();
                        return;
                    }
                    else if (newState.pageURL == "/MDEAnalytics/ECLDSTopic.jsp?TOPICID=496") {

                        if (window.location.hostname == 'test.eclds.mn.gov')
                            var win = window.open("https://stage.secure.education.mn.gov/MDEAnalytics/ECLDSTopic.jsp?TOPICID=496", '_blank');
                        else if (window.location.hostname == 'eclds.mn.gov')
                            var win = window.open("https://secure.education.mn.gov/MDEAnalytics/ECLDSTopic.jsp?TOPICID=496", '_blank');
                        else
                            var win = window.open("https://edu-websecd01.educ.state.mn.us/MDEAnalytics/ECLDSTopic.jsp?TOPICID=496", '_blank');

                        win.focus();
                        return;
                    }
                    else if (newState.pageURL == "/MDEAnalytics/DataTopic.jsp?TOPICID=514") {

                        if (window.location.hostname == 'test.eclds.mn.gov')
                            var win = window.open("https://test.api.education.mn.gov/MDEAnalytics/DataTopic.jsp?TOPICID=514", '_blank');
                        else if (window.location.hostname == 'eclds.mn.gov')
                            var win = window.open("https://public.education.mn.gov/MDEAnalytics/DataTopic.jsp?TOPICID=514", '_blank');
                        else
                            var win = window.open("http://edu-webpubd01.educ.state.mn.us//MDEAnalytics/DataTopic.jsp?TOPICID=514", '_blank');

                        win.focus();
                        return;
                    }

                    var mess = "This page is not yet implemented.";
                    console.log(mess);
                    alert(mess);

                    // we need to restore the report highlight. note
                    // that the passed report is NOT the one that
                    // failed, it's the one that was there before the
                    // failed attempt
                    Backbone.trigger("workspaceStateChangeFailed", this.currentState);
                    return;
                }

                this.showExtraUI();

                if (this.drawerNeedsToClose()) {
                    this.closeDrawer();
                }

                if (!reconstructing) {
                    _.defer(function () {
                        this.updateWorkspaceURL();
                    }.bind(this));
                }
            },
            /** action: show the help bubble that points to the header of the first
             * data box. this should be run the first time the user sees a data
             * box. */
            showHeaderIntroBubble: function () {
                var targetDataBox = this.dataBoxControllers[0];

                if (!targetDataBox) {
                    var err = "WARNING: Error showing bubble, data box not found.";
                    console.log(err);
                    return;
                }

                // so if this is the first databox we're making for this
                // session, show the instructional bubble.
                this.bubbleMaker.show({
                    direction: "down",
                    pointingAt: targetDataBox.view.$("header"),
                    message: "Touch this bar to look at a certain school or refine your search; touch again when done.",
                    nudge: {
                        x: 50,
                        y: 10
                    },
                    pulsateElement: false,
                    duration: null
                });
            },

            /** action: add a data box to the workspace with the default
             * settings for the selected report. If no report is
             * selected, throw an error
             */
            addBox: function (params) {
                Backbone.trigger("bubblesShouldClose");

                if (this.currentState.reportModel) {

                    var newDataBox = new DataBoxController(params);

                    this.dataBoxControllers.push(newDataBox);

                    // if there isn't one already, we should add the adder too.
                    if ($("#workspace .dataBoxAdder").length === 0) {
                        this.addBoxAdder();
                    }
                    // if we were not given params, we assume (rightly, I hope) that
                    // the data box was a fresh one added by the user, and we should
                    // update the URL once we have the filter loaded up.
                    if (!params) {
                        newDataBox.filtersLoadedPromise
                            .then(function () {
                                this.updateWorkspaceURL();
                            }.bind(this))
                            .then(null, Util.reportPromiseException);
                    }
                    this.adder.update(this.dataBoxControllers.length);

                    return newDataBox;
                } else {
                    throw {
                        name: "No Report Error",
                        message: "There needs to be a report selected before you try to create a data box."
                    };
                }
            },
            /** action: remove a data box.
             * @param {module:cardboard/dataBoxController} - the controller object of
             * the data box to be removed.
             */
            removeBox: function (controller) {
                var index = _(this.dataBoxControllers).indexOf(controller);
                this.dataBoxControllers.splice(index, 1);
                if (this.dataBoxControllers.length < settings.get("maxBoxes")) {
                    this.adder.view.$el.removeClass("maxedOut");
                }
                this.updateWorkspaceURL();
            },
            /** action: remove all data boxes on the workspace */
            removeAllBoxes: function () {
                // clean up any controllers still in there
                _(this.dataBoxControllers).each(function (dbc) {
                    dbc.destroy();
                });

                // and then start fresh!
                this.dataBoxControllers = [];
            },
            /** create the databox adder controller */
            addBoxAdder: function () {
                /** a reference to the data box adder controller object
                 * @type Object
                 */
                this.adder = new MiscControllers.DataBoxAdder();
            },

            /** action: show a workspace page. DO NOT USE FROM OUTSIDE THIS
             * CLASS. Use setWorkspacePage() instead.
             * @param {String} pageID
             * @private
             * */
            showWorkspacePage: function (pageID) {
                $("#workspace .page." + pageID).css("display", "");
                this.hideOtherPages(pageID);

                if (pageID === "using") {
                    var showBubble = function () {
                        this.bubbleMaker.show({
                            direction: "up",
                            pointingAt: "#mainHeader > .edgeButton:first-child",
                            message: "Open the side menu to choose a report.",
                            duration: null, // don't auto-close
                            nudge: {
                                x: 5,
                                y: 0
                            }
                        });
                    }.bind(this);
                    if (this.drawerNeedsToClose()) {
                        Backbone.once("hamburgerDrawerClosed", showBubble);
                    } else if (this.navigationIsOffscreen()) {
                        showBubble();
                    }
                }
                Backbone.trigger("introShown");
            },

            /** action: hide workspace pages other than the given one
             * @param {String} pageID
             * @private */
            hideOtherPages: function (pageID) {
                $("#workspace .page").not(".page." + pageID).hide();
            },
            /** action: close the report drawer
             * @private */
            closeDrawer: function () {
                this.$navDrawer.panel("close");
            },
            /** action: set keyboard focus to drawer toggle
             * @private */
            focusDrawerToggle: function () {
                $("#mainHeader .drawerToggle").focus();
            },
            /** action: hide question bar and data box adder
             * @private */
            hideExtraUI: function () {
                $("html > body").addClass("extraUIHidden");
            },
            /** action: show question bar and data box adder
             * @private */
            showExtraUI: function () {
                $("html > body").removeClass("extraUIHidden");
            },
            /** push content into the global modal info dialog box
             * @param {String} the content */
            setModalInfoDialogContent: function (content) {
                var $mess = this.$modalInfoDialog.children(".message");
                $mess.html(content);
            },
            /** give the Modal content container a class
             * @param {String} the class */
            setModalInfoDialogClass: function (className) {
                this.$modalInfoDialog.children(".message").addClass(className);
            },
            /** give the Modal content container a class
             * @param {String} the class */
            setModalInfoDialogDynamicPath: function (path) {
                this.$modalInfoDialog.children(".message").attr('data-dynamickey', path);
            },
            /** action: show the global modal info dialog box */
            showModalInfoDialog: function () {
                this.$modalInfoDialog.popup("open");
            },
            /** set fixed header visiblity
             * @param {Boolean} visible - whether the fixed header should be visible
             * @private */
            setFixedHeaderVisibility: function (visible) {
                var $mh = $("#mainHeader"),
                    command = (visible ? "show" : "hide");

                if ($mh.is("[data-position = fixed]")) {
                    $mh.toolbar(command);
                }

                // The Android browser needs a little extra help here. When the
                // legacy flexbox layout is used for the header bar, it has trouble
                // toggling between position: fixed and position: absolute. So, the
                // non-instrusive workaround is to just hide the header like this
                // until we come up with a more proper way of fixing this. Maybe we
                // just don't provide a legacy flexbox layout.
                if (Bowser.android && !Bowser.chrome) {
                    if (command === "show") {
                        $mh.css({
                            visibility: "visible"
                        });
                    } else if (command === "hide") {
                        $mh.css({
                            visibility: "hidden"
                        });
                    }
                }

                return Promise.resolve();
            },
            // setFixedHeaderVisibility: function (visible) {
            // 	var $mh = $("#mainHeader"),
            // 		height = $mh.outerHeight(),
            // 		animation;

            // 	if ($mh.is("[data-position = fixed]")) {
            // 		if (visible) {
            // 			animation = { translateY: "0" };
            // 		} else {
            // 			animation = { translateY: -height + "px" };
            // 		}
            // 		return Promise.cast($mh.velocity(animation).promise());
            // 	}
            // 	return Promise.resolve();
            // },

            /** action: show the fixed header bar */
            showFixedHeader: function () {
                return this.setFixedHeaderVisibility(true);
            },
            /** action: hide the fixed header bar */
            hideFixedHeader: function () {
                return this.setFixedHeaderVisibility(false);
            }
        });

        return MainWorkspaceController;
    });
